import {
  buildCursorPath,
  catalogBySlug,
  LogSet,
  paginate,
  similarTodayAnchor,
  TODAY_ANCHOR_WEEKS,
  todayHistoryFromSets,
  WEEK_MS,
  type Exercise,
  type LogSet as LogSetT,
  type TodayAnchor,
  type TodayAnchorSet,
  type TodayHistory,
  type TodayProposalView,
} from '@heraion/shared';

import type { ProgramApiFetch } from '../ProgramEditor/types';
import { FETCH_CONCURRENCY, mapBounded } from './lastLoggedBeyondWindow';
import { readLastSetsSnapshot } from './lastSetsSnapshot';
import { loadExerciseSets, readCachedExerciseSets } from './useExerciseSetsFallback';

// P0 57d74690: each candidate's history for «Build today's session». One
// read of every set in the anchor window, then the indexed door only for
// what that window does not hold.

const SETS_PAGE_SIZE = 500;
const SETS_MAX_PAGES = 10;
const WINDOW_MS = TODAY_ANCHOR_WEEKS * WEEK_MS;

/** Every set since `sinceMs`, all exercises. Null: the read failed. */
async function setsSince(apiFetch: ProgramApiFetch, sinceMs: number): Promise<LogSetT[] | null> {
  const query = `since=${encodeURIComponent(new Date(sinceMs).toISOString())}&limit=${SETS_PAGE_SIZE}`;
  try {
    const { items } = await paginate<unknown, string>(
      async (cursor) => {
        const res = await apiFetch<{ items?: unknown[]; cursor?: string | null }>(
          buildCursorPath('/api/sets', query, cursor),
        );
        return { items: res.items ?? [], cursor: res.cursor ?? null };
      },
      { maxPages: SETS_MAX_PAGES },
    );
    return items.flatMap((raw) => {
      const parsed = LogSet.safeParse(raw);
      return parsed.success ? [parsed.data] : [];
    });
  } catch {
    return null;
  }
}

// A drop-in set has no session: its day stands in, so practice counts days.
const fromLogSet = (l: LogSetT): TodayAnchorSet => {
  const { kind, performedAt, sessionId, ...rest } = l;
  return {
    ...rest,
    type: kind,
    atMs: Date.parse(performedAt),
    session: sessionId ?? `day:${performedAt.slice(0, 10)}`,
  };
};

const push = (by: Map<string, TodayAnchorSet[]>, slug: string, set: TodayAnchorSet) => {
  const list = by.get(slug);
  if (list) list.push(set);
  else by.set(slug, [set]);
};

export type TodayHistoryLoad = {
  history: Map<string, TodayHistory>;
  /** #360: the window was not the server's (known offline, or its read
   *  failed), so the history came from this device. */
  fromCache: boolean;
  /** #360: slugs this device holds nothing for. "Don't know", never "never
   *  done". Empty unless `fromCache`. */
  unknown: ReadonlySet<string>;
};

type LoadOpts = {
  slugs: readonly string[];
  catalog: readonly Exercise[];
  apiFetch: ProgramApiFetch;
  nowMs: number;
  /** #360: the host knows the network is not there. Nothing is asked: every
   *  answer comes off the disk at once, instead of each GET waiting out its
   *  deadline (GET_DEADLINE_MS) one after the other. */
  offline?: boolean;
};

const unionById = (a: readonly LogSetT[], b: readonly LogSetT[]): LogSetT[] => {
  const byId = new Map<string, LogSetT>();
  for (const s of [...a, ...b]) byId.set(s.id, s);
  return [...byId.values()];
};

export async function loadTodayHistorySourced(opts: LoadOpts): Promise<TodayHistoryLoad> {
  const { slugs, catalog, apiFetch, nowMs, offline = false } = opts;
  const snapshot = await readLastSetsSnapshot();
  const windowSets = offline ? null : await setsSince(apiFetch, nowMs - WINDOW_MS);
  const fromCache = windowSets === null;
  const sets = new Map<string, TodayAnchorSet[]>();
  for (const l of windowSets ?? []) push(sets, l.exerciseSlug, fromLogSet(l));
  // No set in the window, or the read failed: the indexed door, for the last
  // session at any age (D1). A cache younger than the window cannot miss a
  // set older than it, so that is its age limit.
  const rest = slugs.filter((slug) => !sets.has(slug));
  // #360: the window snapshot answers first. Younger than the window, beside a
  // window that answered, it cannot miss a newer set of a slug the window
  // lacks (the window would hold it), so the door stays shut.
  const fresh = windowSets !== null && snapshot !== null && nowMs - snapshot.savedAtMs <= WINDOW_MS;
  const older = await mapBounded(rest, FETCH_CONCURRENCY, async (slug) => {
    const snap = snapshot?.setsBySlug.get(slug);
    if (offline) {
      const door = await readCachedExerciseSets(slug);
      return snap === undefined && door === null ? null : unionById(snap ?? [], door ?? []);
    }
    if (fresh && snap !== undefined) return snap;
    const served = await loadExerciseSets(slug, apiFetch, { maxAgeMs: WINDOW_MS });
    if (windowSets !== null || served.length > 0) return served;
    // The window failed and the door brought nothing: the snapshot, at any age.
    return snap ?? null;
  });
  const unknown = new Set<string>();
  rest.forEach((slug, i) => {
    const found = older[i];
    if (found === null || found === undefined) {
      if (fromCache) unknown.add(slug);
      return;
    }
    for (const l of found) push(sets, slug, fromLogSet(l));
  });

  const own = new Map<string, TodayHistory>();
  for (const [slug, list] of sets) own.set(slug, todayHistoryFromSets(slug, list, nowMs));
  const anchors = new Map<string, TodayAnchor | null>(
    [...own].map(([slug, h]) => [slug, h.anchor]),
  );
  const index = catalogBySlug(catalog);
  const out = new Map<string, TodayHistory>();
  for (const slug of slugs) {
    const h = own.get(slug) ?? { anchor: null, practice: 0 };
    const ex = index.get(slug);
    out.set(
      slug,
      h.anchor || !ex ? h : { ...h, anchor: similarTodayAnchor(ex, catalog, anchors) },
    );
  }
  return { history: out, fromCache, unknown };
}

type TodayLoadSource = NonNullable<TodayProposalView['loadSource']>;

/** #360: what the draft says about its loads. Partial: a row holds an exercise
 *  this device knew nothing about. */
export const todayLoadSource = (
  load: Pick<TodayHistoryLoad, 'fromCache' | 'unknown'>,
  rowSlugs: readonly string[],
): TodayLoadSource => {
  if (!load.fromCache) return 'network';
  return rowSlugs.some((slug) => load.unknown.has(slug)) ? 'partial' : 'cache';
};
